import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import {
  downloadActualDataFile,
  type DownloadActualDataFileInput,
  type DownloadActualDataTemplateFileInput,
  downloadAllActualDataFiles,
  type DownloadAllActualDataFilesInput,
  getActualData,
  getActualDataById,
  type GetActualDataInput,
  getActualDataOutput,
  getActualDataSchemas,
  type GetActualDataSchemasInput,
  getDownloadingActualDataTemplate,
  importActualData,
  type ImportActualDataInput,
} from '../../api/actualData';
import {
  type TypeActualDataFile,
  type TypeActualDataSchemaFile,
} from '../../common/dataTypes';
import { downloadFile, getCompositionFileName } from '../../utils/helpers';
import { type DefaultMutationError, type DefaultQueryError } from '..';

export const useGetActualData = (
  input: GetActualDataInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeActualDataFile[]>, DefaultQueryError>
  >
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['getActualData', input],
    queryFn: async () => {
      const { data } = await getActualData(input);

      return data;
    },
    ...opts,
  });

export const useGetActualDataById = (
  input: DownloadActualDataFileInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeActualDataFile>, DefaultQueryError>
  >
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['getActualDataById', JSON.stringify(input)],
    queryFn: async () => {
      const { data } = await getActualDataById(input);

      return data;
    },
    ...opts,
  });

export const useGetActualDataOutput = (
  input: GetActualDataInput,
  opts?: Partial<UseQueryOptions<Awaited<any>, DefaultQueryError>>
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['getActualDataOutput', JSON.stringify(input)],
    queryFn: async () => {
      const { data }: AxiosResponse<any> = await getActualDataOutput(input);

      return data;
    },
    ...opts,
  });

export const useGetDownloadingActualDataTemplate = (
  input: DownloadActualDataTemplateFileInput,
  opts?: Partial<
    UseQueryOptions<{ fileLink: string; filename: string }, DefaultQueryError>
  >
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['downloadActualDataTemplate', JSON.stringify(input)],
    queryFn: async () => {
      const res: AxiosResponse<Blob> =
        await getDownloadingActualDataTemplate(input);

      const contentDisposition = res.headers['content-disposition'];

      const filename = getCompositionFileName(contentDisposition);

      return {
        fileLink: URL.createObjectURL(res.data),
        filename,
      };
    },
    ...opts,
  });

export const useDownloadActualDataFile = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<{
        fileLink: string;
        filename: string;
      }>,
      DefaultMutationError,
      any,
      any
    >
  >
) =>
  useMutation({
    mutationKey: ['downloadActualData'],
    mutationFn: async (input: DownloadActualDataFileInput) => {
      const res: AxiosResponse<Blob> = await downloadActualDataFile(input);

      const contentDisposition = res.headers['content-disposition'];

      const filename = getCompositionFileName(contentDisposition);

      return {
        fileLink: URL.createObjectURL(res.data),
        filename,
      };
    },
    onSuccess(data, variables, unknown, context) {
      const { fileLink, filename } = data;

      downloadFile({
        fileLink,
        filename,
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });

export const useDownloadAllActualDataFiles = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<{
        fileLink: string;
        filename: string;
      }>,
      DefaultMutationError,
      any,
      any
    >
  >
) =>
  useMutation({
    mutationKey: ['downloadAllActualDataFiles'],
    mutationFn: async (input: DownloadAllActualDataFilesInput) => {
      const res: AxiosResponse<Blob> = await downloadAllActualDataFiles(input);

      const contentDisposition = res.headers['content-disposition'];

      const filename = getCompositionFileName(contentDisposition);

      return {
        fileLink: URL.createObjectURL(res.data),
        filename,
      };
    },
    onSuccess(data, variables, unknown, context) {
      const { fileLink, filename } = data;

      downloadFile({
        fileLink,
        filename,
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });

export const useImportActualData = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['importActualData'],
    mutationFn: async (input: ImportActualDataInput) => {
      const { data }: AxiosResponse = await importActualData(input);

      return data;
    },
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: [
          'getActualDataSchemaFiles',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      queryClient.refetchQueries({
        queryKey: [
          'getActualData',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useGetActualDataSchemaFiles = (
  input: GetActualDataSchemasInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeActualDataSchemaFile[]>, DefaultQueryError>
  >
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['getActualDataSchemaFiles', input],
    queryFn: async () => {
      const { data } = await getActualDataSchemas(input);

      return data.files;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...opts,
  });
